<!-- HomeView.vue -->
<template>
  <div class="min-h-screen bg-gray-100">
    <!-- Side Navigation (Desktop Only) -->
    <SideNavbar currentRoute="home" />

    <!-- Main Content Area -->
    <div class="md:ml-20">
      <!-- Desktop View Component -->
      <TestDesktopView
        class="hidden md:block"
        :user="user"
        :currentTime="currentTime"
        :currentDate="currentDate"
        :currentDay="currentDay"
        :currentMonth="currentMonth"
        :currentWeather="currentWeather"
        :weatherLoading="weatherLoading"
        :weatherLocation="weatherLocation"
        :weatherError="weatherError"
        @retry-weather="fetchWeatherForCard"
        @train-clicked="handleTrainClick"
        @metro-clicked="handleMetroClick"
      />

      <!-- Mobile View Component -->
      <TestMobileView
        class="md:hidden"
        :user="user"
        :currentTime="currentTime"
        :currentDate="currentDate"
        :currentDay="currentDay"
        :currentMonth="currentMonth"
        :currentWeather="currentWeather"
        :weatherLoading="weatherLoading"
        :weatherLocation="weatherLocation"
        :weatherError="weatherError"
        @retry-weather="fetchWeatherForCard"
        @train-clicked="handleTrainClick"
        @metro-clicked="handleMetroClick"
      />
    </div>
  

    <!-- Mobile Navigation (only on mobile) -->
    <div class="md:hidden fixed bottom-0 left-0 right-0 z-10">
      <Navbar currentRoute="home" />
    </div>
  </div>
</template>
<script>
import { computed } from 'vue';
import { useStore } from 'vuex';
import SideNavbar from '@/components/SideNavbar.vue';
import Navbar from '@/components/Navbar.vue';
import TestDesktopView from '@/components/TestDesktopView.vue';
import TestMobileView from '@/components/MobileView.vue';
import { getCurrentLocation, fetchCurrentWeather } from '@/services/weatherService';
import { hasUserMoved, validateCoordinates } from '@/utils/geolocation';
import { normalizeLocationName } from '@/utils/locationUtils';

export default {
  name: 'HomeView',
  components: {
    SideNavbar,
    Navbar,
    TestDesktopView,
    TestMobileView
  },
  setup() {
    const store = useStore();
    const user = computed(() => store.getters.getUser);

    return {
      user
    };
  },
  data() {
    return {
      currentWeather: null,
      weatherLoading: false,
      weatherLocation: '',
      weatherError: null,
      currentTime: '',
      currentDate: '',
      currentDay: '',
      currentMonth: ''
    };
  },
  mounted() {
    // Initialize time and date
    this.updateDateTime();
    // Update time every second
    this.timeInterval = setInterval(this.updateDateTime, 1000);

    // Fetch weather data immediately when component mounts
    this.fetchWeatherForCard();

    // Also try to load any existing cached data immediately for faster display
    this.loadCachedWeatherIfAvailable();
  },
  beforeUnmount() {
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
  },
  methods: {
    updateDateTime() {
      const now = new Date();

      // Format time (HH:MM)
      this.currentTime = now.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });

      // Format date (DD)
      this.currentDate = now.getDate().toString();

      // Format day (Monday, Tuesday, etc.)
      this.currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });

      // Format month (January, February, etc.)
      this.currentMonth = now.toLocaleDateString('en-US', { month: 'long' });
    },

    handleTrainClick() {
      // Handle train tile click - could open train search popup
      console.log('Train tile clicked');
    },

    handleMetroClick() {
      // Handle metro tile click - could open metro search popup
      console.log('Metro tile clicked');
    },

    loadCachedWeatherIfAvailable() {
      // Try to load cached weather data immediately for faster initial display
      try {
        const cached = localStorage.getItem('travley_user_weather_cache');
        if (cached) {
          const data = JSON.parse(cached);
          if (data.weather && data.weather.main && data.weather.weather) {
            // Only show cached data if it's relatively recent (within 6 hours)
            const now = new Date().getTime();
            const cacheTime = new Date(data.timestamp).getTime();
            const sixHours = 6 * 60 * 60 * 1000;

            if (now - cacheTime < sixHours) {
              this.currentWeather = data.weather;
              this.weatherLocation = data.location || 'Cached Location';
              this.weatherError = null;
            }
          }
        }
      } catch (error) {
        // If cached data is corrupted, ignore and let fresh fetch handle it
      }
    },

    async fetchWeatherForCard() {
      this.weatherLoading = true;
      this.weatherError = null;

      try {
        // Get current user location
        const currentCoordinates = await getCurrentLocation();

        // Validate coordinates
        if (!validateCoordinates(currentCoordinates)) {
          throw new Error('Invalid location coordinates received');
        }

        // Check for cached weather data with location and time validation
        const cachedData = this.getCachedUserLocationWeatherData(currentCoordinates);
        if (cachedData) {
          this.currentWeather = cachedData.weather;
          this.weatherLocation = cachedData.location;
          this.weatherError = null;
          return;
        }

        // Fetch fresh weather data
        const weather = await fetchCurrentWeather(currentCoordinates.lat, currentCoordinates.lon);

        // Validate weather response
        if (!weather || !weather.main || !weather.weather || !weather.weather[0]) {
          throw new Error('Invalid weather data received from API');
        }

        this.currentWeather = weather;
        this.weatherError = null;

        this.weatherLocation = normalizeLocationName(weather.name);

        // Cache the weather data with coordinates
        this.cacheUserLocationWeatherData(weather, this.weatherLocation, currentCoordinates);
      } catch (error) {
        this.weatherError = error.message || 'Failed to load weather data';
        this.currentWeather = null;
        this.weatherLocation = '';

        // Try to load any existing cached data as fallback
        try {
          const fallbackCache = localStorage.getItem('travley_user_weather_cache');
          if (fallbackCache) {
            const data = JSON.parse(fallbackCache);
            if (data.weather && data.weather.main && data.weather.weather) {
              this.currentWeather = data.weather;
              this.weatherLocation = data.location || 'Cached Location';
              this.weatherError = null;
            }
          }
        } catch (fallbackError) {
          // Fallback failed, keep error state
        }
      } finally {
        this.weatherLoading = false;
      }
    },
    getCachedUserLocationWeatherData(currentCoordinates) {
      try {
        const cached = localStorage.getItem('travley_user_weather_cache');
        if (!cached) return null;

        const data = JSON.parse(cached);
        const now = new Date().getTime();
        const cacheTime = new Date(data.timestamp).getTime();
        const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds

        // Check if data is less than 1 hour old
        const isTimeValid = now - cacheTime < oneHour;

        // Check if user location hasn't moved more than 3km
        const isLocationValid = !hasUserMoved(currentCoordinates, data.coordinates, 3);

        // Return cached data only if both time and location are valid
        if (isTimeValid && isLocationValid) {
          return {
            weather: data.weather,
            location: data.location
          };
        } else {
          // Remove stale or location-invalid data
          localStorage.removeItem('travley_user_weather_cache');
          return null;
        }
      } catch (error) {
        // If there's an error parsing cached data, remove it
        localStorage.removeItem('travley_user_weather_cache');
        return null;
      }
    },
    cacheUserLocationWeatherData(weather, location, coordinates) {
      try {
        // Only cache if coordinates are valid
        if (!validateCoordinates(coordinates)) {
          return;
        }

        const cacheData = {
          weather,
          location,
          coordinates: {
            lat: coordinates.lat,
            lon: coordinates.lon
          },
          timestamp: new Date().toISOString(),
          cacheType: 'user_location'
        };
        localStorage.setItem('travley_user_weather_cache', JSON.stringify(cacheData));
      } catch (error) {
        // Error caching data - continue silently
      }
    }
  }
}
</script>
<style scoped>
/* Wireframe styling for visual clarity */
.wireframe-container {
  border: 2px dashed #d1d5db;
  background-color: #f9fafb;
}

/* Responsive adjustments */
@media (min-width: 768px) {
  .min-h-screen {
    padding-bottom: 0;
  }
}

/* Smooth transitions */
* {
  transition: all 0.2s ease;
}

/* Hover effects for wireframe tiles */
.bg-white:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
</style>
